import React, { useMemo } from 'react'

import { Fragment, useState, useCallback, useEffect } from 'react'

import { IoClose } from 'react-icons/io5'

import { v4 as uniqid } from 'uuid'

import ModelPicker from '@/Form/V2/ModelPicker'
import SubmitForm  from '@/Form/V2/SubmitForm'
import DatePicker  from '@/Form/V2/DatePicker'
import Input       from '@/Form/V2/Input'

import getFormStatus from '@/Form/Functions/getFormStatus'

import Loader from '@Pure/Loader'

import FormsStore from '@Store/Forms'

import { isTrue, num } from '@root/utility'

const FORM_NAME = 'PrivateSale';

function getFormInit ()
{
	const FORM_INIT =
	{
		dates:
		[
			getNewDate()
		],

		is__started: false
	};

	return FORM_INIT;
}

export default function PrivateSaleForm ({ onUpdate, ...props })
{
	const [submit, setSubmit] = useState();
	const [submitting, setSubmitting] = useState(false);
	const [status, setStatus] = useState({ });

	const fields_status = status?.fields || { };

	const store_dates = FormsStore.useState(s => s[FORM_NAME]?.dates || []);

	const opening_date = FormsStore
	.useState(s => s[FORM_NAME]?.['opening[date]'] || '');

	const is__started = FormsStore.useState(s => isTrue(s[FORM_NAME]?.is__started));

	useEffect(function ()
	{
		if (props.type === 'create')
		{
			FormsStore.update(function (store)
			{
				store[FORM_NAME] = getFormInit();
			});
		}
	},
	[props.type]);

	const removeDateRow = useCallback(function (e)
	{
		if (is__started) return;

		const id = e.target?.getAttribute?.('data-id');

		FormsStore.update(function (store)
		{
			store[FORM_NAME] ||= { };

			store[FORM_NAME].dates = store[FORM_NAME].dates
			.filter(row => row.id !== id);

			setStatus(function (status)
			{
				if (!status.fields) return status;

				Object.keys(status.fields)
				.filter(field => field.includes('[' + id + ']'))
				.forEach(field => delete status.fields[field]);

				return status;
			});
		});
	},
	[is__started]);

	const dates = useMemo(function ()
	{
		const is__disabled = is__started || (store_dates || []).length < 2;

		return store_dates.map(function (date_row, i)
		{
			const names = getDateNames(date_row, i);

			return (
				<div className="c-vp-date" key={ date_row.id }>

					<div className="c-vp-date__col-date">
						<DatePicker name={ names.date }
							label="Jour"
							form={ FORM_NAME }
							error={ fields_status[names.date]?.error }
							bemit={ ['auto'] }
							value={ date_row.date }
							onChange={ handleDateChange.bind(setStatus) }
							read-only={ is__started }
						/>
					</div>

					<div className="c-vp-date__col-start_hour">
						<Input name={ names.start.h } label="Début - heure"
							form={ FORM_NAME }
							error={ fields_status[names.start.h]?.error }
							safe-number int
							bemit={ ['33%'] }
							value={ date_row.start.h }
							onChange={ handleDateChange.bind(setStatus) }
							placeholder="Ex : 15"
							read-only={ is__started }
						/>
					</div>

					<div className="c-vp-date__col-unit">
						h
					</div>

					<div className="c-vp-date__col-start_min">
						<Input name={ names.start.m } label="Début - minute"
							form={ FORM_NAME }
							error={ fields_status[names.start.m]?.error }
							safe-number int
							bemit={ ['33%'] }
							value={ date_row.start.m }
							onChange={ handleDateChange.bind(setStatus) }
							placeholder="Ex : 30"
							read-only={ is__started }
						/>
					</div>

					<div className="c-vp-date__col-unit">
						mn
					</div>

					<div className="c-vp-date__col-sep" />

					<div className="c-vp-date__col-end_hour">
						<Input name={ names.end.h } label="Fin - heure"
							form={ FORM_NAME }
							error={ fields_status[names.end.h]?.error }
							safe-number int
							bemit={ ['33%'] }
							value={ date_row.end.h }
							onChange={ handleDateChange.bind(setStatus) }
							placeholder="Ex : 15"
							read-only={ is__started }
						/>
					</div>

					<div className="c-vp-date__col-unit">
						h
					</div>

					<div className="c-vp-date__col-end_min">
						<Input name={ names.end.m } label="Fin - minute"
							form={ FORM_NAME }
							error={ fields_status[names.end.m]?.error }
							safe-number int
							bemit={ ['33%'] }
							value={ date_row.end.m }
							onChange={ handleDateChange.bind(setStatus) }
							placeholder="Ex : 30"
							read-only={ is__started }
						/>
					</div>

					<div className="c-vp-date__col-unit">
						mn
					</div>

					<div className="c-vp-date__col-remove">
						<button type="button" aria-label="Retirer la ligne"
							className={ 'c-vp-date__remove' + (is__disabled ? ' is-disabled' : '') }
							onClick={ removeDateRow }
							data-id={ date_row.id }
							disabled={ is__disabled }>
							<IoClose className="c-vp-date__remove-icon" />
							<span className="u-readers-only">
								Retirer la ligne
							</span>
						</button>
					</div>

				</div>
			);
		});
	},
	[store_dates, fields_status, is__started]);

	const handleOpeningDateChange = useCallback(function ({ e, value, name })
	{
		if (is__started) return;

		FormsStore.update(function (store)
		{
			setStatus(function (status)
			{
				if (!status.fields?.['opening[date]']) return status;

				delete status.fields['opening[date]'];

				return status;
			});

			store[FORM_NAME]['opening[date]'] = value;
		});
	},
	[is__started]);

	const addDate = useCallback(function ()
	{
		if (is__started) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME] ||= { };

			store[FORM_NAME].dates ||= [];

			store[FORM_NAME].dates.push(getNewDate());
		});
	},
	[is__started]);

	const sendForm = useCallback(function (e)
	{
		if (submitting) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME]._submit_id = uniqid();
		});

		setSubmitting(true);
		setSubmit(uniqid());
	},
	[submitting]);

	const onError = useCallback(function (update)
	{
		setSubmit(undefined);
		setStatus(update);
		setSubmitting(false);
	},
	[]);

	const onSuccess = useCallback(function (response)
	{
		setSubmit(undefined);
		setStatus(response);
		setSubmitting(false);

		setTimeout(function ()
		{
			FormsStore.update(function (store)
			{
				store[FORM_NAME] ||= { };

				store[FORM_NAME].dates = (store[FORM_NAME].dates || [])
				.map(function (row)
				{
					return { ...row };
				});
			});
		},
		100);

		if (props.type === 'update') return onUpdate?.();

		FormsStore.update(function (store)
		{
			store[FORM_NAME] = getFormInit();
		});
	},
	[onUpdate]);

	return (
		<Fragment>

			<div className="c-form c-form--private-sale">

				<h2 className="c-title c-title--flex-form">
					Vente Privée
				</h2>

				<div className="c-form__content">

					<Input name="title" label="titre"
						form={ FORM_NAME }
						error={ status?.fields?.['title']?.error }
						bemit={ ['50%'] }
						className="u-margin-bottom-20"
					/>

					<fieldset className="o-form__fieldset 50%">

						<legend className="o-form__fieldset-legend">
							Ouverture des inscriptions
						</legend>

						<div className="o-form__fieldset-content">

							<DatePicker name="opening[date]"
								label="Jour d'ouverture"
								form={ FORM_NAME }
								error={ status?.fields?.['opening[date]']?.error }
								bemit={ ['auto'] }
								onChange={ handleOpeningDateChange }
								value={ opening_date }
								read-only={ is__started }
							/>

							<Input name="opening[h]" label="Heure"
								form={ FORM_NAME }
								error={ status?.fields?.['opening[h]']?.error }
								safe-number int
								bemit={ ['33%'] }
								read-only={ is__started }
							/>

							<Input name="opening[m]" label="Minute"
								form={ FORM_NAME }
								error={ status?.fields?.['opening[m]']?.error }
								safe-number int
								bemit={ ['33%'] }
								read-only={ is__started }
							/>

							<Input name="countdown_days"
								label="Affichage anticipé (décompte en jours)"
								form={ FORM_NAME }
								error={ status?.fields?.['countdown_days']?.error }
								bemit={ ['100%'] }
								className="u-margin-bottom-20"
								safe-number int
							/>

						</div>

					</fieldset>

					<Input name="address" label="Lieu (adresse)"
						form={ FORM_NAME }
						error={ status?.fields?.['address']?.error }
						bemit={ ['50%'] }
						className="u-margin-bottom-20"
					/>

					<Input name="payment_infos" label="Paiements acceptés"
						form={ FORM_NAME }
						error={ status?.fields?.['payment_infos']?.error }
						bemit={ ['50%'] }
						className="u-margin-bottom-20"
					/>

					<fieldset className="o-form__fieldset u-margin-top-20">

						<legend className="o-form__fieldset-legend">
							Configuration des créneaux
						</legend>

						<Input name="max_people"
							label="Nombre de places par créneau"
							form={ FORM_NAME }
							error={ status?.fields?.['max_people']?.error }
							safe-number int
							bemit={ ['100%'] }
						/>

						<div className="o-form__fieldset-content">

							{ dates }

							{
								!is__started ?
								(
									<button type="button"
										onClick={ addDate }
										className="c-btn c-btn--add u-no-margin-top">
										Ajouter une ligne
									</button>
								)
								: undefined
							}

						</div>

					</fieldset>

				</div>

			</div>

			{
				submit ?
				(
					<SubmitForm form={ FORM_NAME } id={ submit }
						key={ props.type }
						onError={ onError }
						onSuccess={ onSuccess }
						url={
							'/private-sales/' +
							(props.type === 'create' ? 'create' : 'update')
						}
					/>
				)
				: undefined
			}

			{ getFormStatus(status) }

			<button type="button" className="c-btn c-btn--extra c-btn--green
				u-margin-top-30"
				onClick={ sendForm }
				disabled={ submitting }>
				{
					props.type === 'update'
					? 'Mettre à jour'
					: 'Valider et créer cette vente privée'
				}
				{
					submitting ?
					(
						<Loader bemit={ ['bg-green', 'white'] }
							className="c-loader"
						/>
					)
					: undefined
				}
			</button>

		</Fragment>
	);
}

function handleDateChange (params)
{
	const setStatus = this;

	const name = params.name || params?.target?.name || '';
	const value = params.value || params?.target?.value || '';

	let id = name.split('dates[')[1];

	id = id.slice(0, id.indexOf(']'));

	if (!id) return;

	if (typeof setStatus === 'function')
	{
		setStatus(function (status)
		{
			if (!status.fields?.[name]) return status;

			delete status.fields[name];

			return status;
		});
	}

	FormsStore.update(function (store)
	{
		store[FORM_NAME] ||= { };

		store[FORM_NAME].dates ||= [];

		const row = store[FORM_NAME].dates.find(row => row.id === id);

		if (!row) return store;

		if (name.endsWith('[date]'))
		{
			row.date = value;
		}
		else if (name.includes('[start]') || name.includes('[end]'))
		{
			const keyword = name.includes('[start]') ? 'start' : 'end';

			row[keyword] ||= { };

			const field = name.split('[' + keyword + '][')[1].slice(0, -1);

			row[keyword][field] = value === ''
			? ''
			: String(num(value, 0));
		}
	});
}

function getNewDate ()
{
	const row =
	{
		id: uniqid(),
		date: '',

		start:
		{
			h: '',
			m: ''
		},

		end:
		{
			h: '',
			m: ''
		}
	};

	return row;
}

function getDateNames (date_row, index)
{
	const prefix = 'dates[' + date_row.id + ']';

	const names =
	{
		date: prefix + '[date]',

		start:
		{
			h: prefix + '[start][h]',
			m: prefix + '[start][m]'
		},

		end:
		{
			h: prefix + '[end][h]',
			m: prefix + '[end][m]'
		}
	};

	return names;
}